import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Users } from 'lucide-react';
import { Button } from './Button';
import { useAuth } from '../contexts/AuthContext';
import { db, isFirebaseEnabled } from '../lib/firebase';
import { collection, addDoc, query, orderBy, onSnapshot, serverTimestamp, where } from 'firebase/firestore';
interface Message {
  id: string;
  text: string;
  userId: string;
  userName: string;
  userAvatar: string;
  timestamp: any;
}
interface QuestChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  questId: string;
  questTitle: string;
  participants: Array<{
    id: string;
    name: string;
    avatar: string;
  }>;
}
export function QuestChatModal({
  isOpen,
  onClose,
  questId,
  questTitle,
  participants
}: QuestChatModalProps) {
  const {
    user
  } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  useEffect(() => {
    if (!isOpen || !isFirebaseEnabled || !db) {
      // Mock messages for local mode
      setMessages([{
        id: '1',
        text: 'Hey! Ready to crush this quest together? 💪',
        userId: 'mock-user-1',
        userName: 'Alex',
        userAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Alex',
        timestamp: new Date(Date.now() - 3600000)
      }, {
        id: '2',
        text: "Absolutely! Let's do this! 🚀",
        userId: user?.uid || 'current-user',
        userName: user?.displayName || 'You',
        userAvatar: user?.photoURL || 'https://api.dicebear.com/7.x/avataaars/svg?seed=You',
        timestamp: new Date(Date.now() - 1800000)
      }]);
      return;
    }
    // Real-time Firebase subscription
    const messagesRef = collection(db, 'questChats', questId, 'messages');
    const q = query(messagesRef, orderBy('timestamp', 'asc'));
    const unsubscribe = onSnapshot(q, snapshot => {
      const messagesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Message[];
      setMessages(messagesData);
    });
    return unsubscribe;
  }, [isOpen, questId, user]);
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !user) return;
    const messageText = newMessage.trim();
    setNewMessage('');
    setLoading(true);
    if (!isFirebaseEnabled || !db) {
      // Local mode - add mock message
      const mockMessage: Message = {
        id: Date.now().toString(),
        text: messageText,
        userId: user.uid,
        userName: user.displayName || 'You',
        userAvatar: user.photoURL || 'https://api.dicebear.com/7.x/avataaars/svg?seed=You',
        timestamp: new Date()
      };
      setMessages([...messages, mockMessage]);
      setLoading(false);
      return;
    }
    try {
      const messagesRef = collection(db, 'questChats', questId, 'messages');
      await addDoc(messagesRef, {
        text: messageText,
        userId: user.uid,
        userName: user.displayName || 'Anonymous',
        userAvatar: user.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.uid}`,
        timestamp: serverTimestamp()
      });
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setLoading(false);
    }
  };
  const formatTime = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };
  return <AnimatePresence data-id="element-462">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6" onClick={onClose} data-id="element-463">
          <motion.div initial={{
        scale: 0.95,
        y: 20
      }} animate={{
        scale: 1,
        y: 0
      }} exit={{
        scale: 0.95,
        y: 20
      }} className="bg-white dark:bg-[#252936] rounded-3xl w-full max-w-2xl h-[600px] shadow-2xl flex flex-col" onClick={e => e.stopPropagation()} data-id="element-464">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-465">
              <div className="flex-1" data-id="element-466">
                <h2 className="text-xl font-fredoka font-bold text-game-dark dark:text-white" data-id="element-467">
                  Quest Chat
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-quicksand mt-1" data-id="element-468">
                  {questTitle}
                </p>
              </div>
              <div className="flex items-center gap-3" data-id="element-469">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-full" data-id="element-470">
                  <Users className="w-4 h-4 text-gray-600 dark:text-gray-400" data-id="element-471" />
                  <span className="text-sm font-fredoka font-bold text-gray-600 dark:text-gray-400" data-id="element-472">
                    {participants.length}
                  </span>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors" data-id="element-473">
                  <X className="w-6 h-6 text-gray-500 dark:text-gray-400" data-id="element-474" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4" data-id="element-475">
              {messages.map(message => {
            const isOwnMessage = message.userId === user?.uid;
            return <motion.div key={message.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className={`flex gap-3 ${isOwnMessage ? 'flex-row-reverse' : ''}`} data-id="element-476">
                    <img src={message.userAvatar} alt={message.userName} className="w-10 h-10 rounded-full border-2 border-gray-200 dark:border-gray-700 flex-shrink-0" data-id="element-477" />
                    <div className={`flex-1 ${isOwnMessage ? 'items-end' : 'items-start'} flex flex-col`} data-id="element-478">
                      <div className="flex items-center gap-2 mb-1" data-id="element-479">
                        <span className="text-xs font-quicksand font-bold text-gray-600 dark:text-gray-400" data-id="element-480">
                          {isOwnMessage ? 'You' : message.userName}
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500 font-quicksand" data-id="element-481">
                          {formatTime(message.timestamp)}
                        </span>
                      </div>
                      <div className={`px-4 py-2 rounded-2xl max-w-md ${isOwnMessage ? 'bg-game-purple text-white' : 'bg-gray-100 dark:bg-gray-800 text-game-dark dark:text-white'}`} data-id="element-482">
                        <p className="text-sm font-quicksand" data-id="element-483">{message.text}</p>
                      </div>
                    </div>
                  </motion.div>;
          })}
              <div ref={messagesEndRef} data-id="element-484" />
            </div>

            {/* Input */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-800" data-id="element-485">
              <div className="flex gap-3" data-id="element-486">
                <input type="text" value={newMessage} onChange={e => setNewMessage(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }} placeholder="Type a message..." className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-[#252936] text-game-dark dark:text-white font-quicksand focus:outline-none focus:border-game-purple" disabled={loading} data-id="element-487" />
                <Button onClick={handleSendMessage} disabled={!newMessage.trim() || loading} icon={<Send className="w-5 h-5" data-id="element-489" />} data-id="element-488">
                  Send
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}